import { CodeReferenceBlockHighlightRecord } from "models/view-models/code-reference-block-highlights/code-reference-block-highlight-record";
import usePrintablePage from "pages/notebooks/printing/use-printable-page";
import { useCallback, useRef, useState } from "react";
import { HighlightColor } from "utilities/enumerations/highlight-color";
import HighlightUtils, {
    DomHighlight,
    PersistedDomHighlight,
} from "utilities/highlight-utils";

/*
========================================================
USAGE

This hook is specifically designed for sections within a code reference block on a Notebooks page. However, the methods can be re-u
========================================================

    // in code-reference-preview.tsx
    const {renderCurrentHighlights,  cleanUpCurrentHighlights, createHighlightFromSelection} = useRegisterCodeReferenceHighlight()

    useEffect(()=>{
        renderCurrentHighlights(highlights);
        return {
            cleanUpCurrentHighlights()
        }
    }, [highlights])

    const handleOnMouseUp = () => {
        const newHighlight = createHighlightFromSelection()
        setHighlights(prev => [...prev, newHightlight])
    }

    <div ref={sectionParentRef} onMouseUp={handleOnMouseUp}>
        <div data-section-id={section.id}>
            {...section here}
            <div data-section-id={subsection.id}>
                {...and so on}
            </div>
        </div>
    </div>

*/

export const DATA_SECTION_ID = "data-section-id";
interface RegisterCodeReferenceHighlightProps {
    printable?: boolean;
}
const useRegisterCodeReferenceHighlight = ({
    printable,
}: RegisterCodeReferenceHighlightProps) => {
    const [
        endingSectionIdOfActiveHighlight,
        setEndingSectionIdOfActiveHighlight,
    ] = useState<number>();
    const clearSelection = () => {
        HighlightUtils.clearSelection();
        setEndingSectionIdOfActiveHighlight(undefined);
    };

    const highlightsRef = useRef<DomHighlight[]>([]);
    const cleanUp = useRef<() => void>();

    const getPersistedHighlights = () => {
        return highlightsRef.current.filter(
            (h): h is PersistedDomHighlight => h.id != null
        );
    };
    // Registers current highlight records within the DOM's CSS to allow them to render
    const renderCurrentHighlights = useCallback(
        (
            codeReferenceBlockContentRef: HTMLDivElement,
            highlightRecords: CodeReferenceBlockHighlightRecord[],
            handleHighlightClick?: (
                highlightId: number,
                coordinates?: { x: number; y: number }
            ) => void,
            newHighlightPriority?: number
        ) => {
            // Sort so highlights are checked newest to oldest
            // (i.e., double highlights will prioritize recently created)
            highlightRecords
                .toSorted((a, b) => a.sortIndex - b.sortIndex)
                .forEach((highlight, index) => {
                    const ranges = HighlightUtils.buildRangeFromHighlightRecord(
                        codeReferenceBlockContentRef,
                        highlight
                    );
                    if (ranges.length) {
                        HighlightUtils.registerCSSHighlightFromRange(
                            `${HighlightUtils.getHighlightColor(
                                highlight.highlightColor
                            )}-${highlight.id}`,
                            ranges,
                            newHighlightPriority ??
                                highlightRecords.length - index
                        );

                        highlightsRef.current.push({
                            id: highlight.id ?? undefined,
                            ranges,
                        });
                    }
                });
            HighlightUtils.applyCustomCssToMatchDynamicHighlightNames(
                highlightRecords
            );

            //Doesn't attach click handler if we're in printable mode
            if (printable) return;

            cleanUp.current = HighlightUtils.attachClickHandler(
                codeReferenceBlockContentRef,
                getPersistedHighlights,
                handleHighlightClick ?? (() => {})
            );
        },
        [printable]
    );

    // Creates a highlight record from a selection of text
    const createHighlightFromSelection = useCallback(() => {
        const range = HighlightUtils.getRangeFromCurrentSelection();

        if (!range) return;

        // Finds start offset
        const {
            sectionId: startingSectionId,
            offsetFromSection: startingSectionOffset,
        } = HighlightUtils.findSectionIdAndOffsetBasedOnRange(
            range.startContainer.parentElement,
            range.startContainer,
            range.startOffset ?? 0,
            DATA_SECTION_ID
        );

        // Finds end offset
        const {
            sectionId: endingSectionId,
            offsetFromSection: endingSectionOffset,
        } = HighlightUtils.findSectionIdAndOffsetBasedOnRange(
            range.endContainer.parentElement,
            range.endContainer,
            range.endOffset ?? 0,
            DATA_SECTION_ID
        );

        if (!startingSectionId || !endingSectionId) {
            return;
        }
        const highlightRecord = new CodeReferenceBlockHighlightRecord().with({
            // configurationId,
            startingSectionId,
            endingSectionId,
            startingSectionOffset,
            endingSectionOffset,
        });

        return highlightRecord;
    }, []);

    const createAndRegisterHighlight = (
        ref: HTMLDivElement | null,
        currentHighlights?: CodeReferenceBlockHighlightRecord[]
    ) => {
        if (!ref) return;

        const highlightRecord = createHighlightFromSelection();

        if (!highlightRecord) {
            return;
        }

        const newHighlightPriority = currentHighlights?.length ?? 1;

        renderCurrentHighlights(
            ref,
            [highlightRecord],
            () => {},
            newHighlightPriority
        ); // Add priority booster here

        clearSelection();

        return highlightRecord;
    };

    const updateTemporaryHighlight = useCallback(
        (
            oldColor: HighlightColor,
            newColor: HighlightColor,
            id: number | undefined,
            priority: number
        ) => {
            const tempHighlight = highlightsRef.current.find(
                (h) => h.id === id
            );
            if (!tempHighlight) return;

            HighlightUtils.moveHighlightToNewRegistry(
                tempHighlight.ranges,
                oldColor,
                newColor,
                priority,
                id ?? undefined
            );
        },
        []
    );

    const clearTemporaryHighlights = () => {
        highlightsRef.current
            .filter((h) => h.id == null)
            .flatMap((h) => h.ranges)
            .forEach((r) => HighlightUtils.deleteFromHighlightRegistry(r));
        highlightsRef.current = highlightsRef.current.filter(
            (h) => h.id != null
        );
    };

    const clearHighlightsForRecord = ({
        highlightId,
        color,
    }: {
        highlightId: number;
        color: HighlightColor | undefined;
    }) => {
        if (!color) return;

        const index = highlightsRef.current.findIndex(
            (h) => h.id === highlightId
        );
        if (index === -1) return;

        const domHighlight = highlightsRef.current[index];

        HighlightUtils.removeHighlightFromRegistry(
            HighlightUtils.getHighlighName(color, highlightId),
            domHighlight.ranges
        );

        highlightsRef.current.splice(index, 1);
    };

    // Removes any previous highlights as to not pollute the DOM
    // To be used in a useEffect cleanup, for instance
    const cleanUpCurrentHighlights = useCallback(() => {
        // I don't know if we need to do this. If they stay in the registry, it might not be a bad thing.
        // If we do, we'll need to send in the ranges we want to clear out, match to registry, and remove

        // agree with the above, just clear the temporary highlights for now
        clearTemporaryHighlights();

        // remove click handler
        if (cleanUp.current) cleanUp.current();
    }, []);

    const refreshEndingSectionIdOfActiveHighlight = useCallback(() => {
        const highlight = createHighlightFromSelection();

        setEndingSectionIdOfActiveHighlight(
            highlight ? highlight?.endingSectionId : undefined
        );
    }, [createHighlightFromSelection]);

    return {
        createHighlightFromSelection,
        renderCurrentHighlights,
        cleanUpCurrentHighlights,
        clearSelection,
        refreshEndingSectionIdOfActiveHighlight,
        endingSectionIdOfActiveHighlight,
        createAndRegisterHighlight,
        clearTemporaryHighlights,
        updateTemporaryHighlight,
        clearHighlightsForRecord,
    };
};

export default useRegisterCodeReferenceHighlight;
